import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { ColumnsType } from 'antd/lib/table'
import { Button, Tooltip } from '@pepper/ui'

import TrendsChart from 'container/SEOTools/components/TrendsChart/TrendsChart'
import GoogleRankPill from 'components/idea-lab/GoogleRankPill'
import KeywordDetailsPopover from 'components/idea-lab/details-popover/KeywordDetailsPopover'
import IntentPill from 'components/idea-lab/IntentPill'

import KeywordDifficulty from '../KeywordDifficulty'

import { formatLongNumber } from 'utils/functions/formatLongNumber'

import { Keyword, KeywordLabel } from 'types/keyword'

import s from './Table.module.css'

type Props = {
  companyId: string
  isGoogleConnected: boolean
  isDomainRemoved: boolean
  totalRecords: number
  isLoadingKeywordLabel: boolean
  onGetKeywordLabel: (suggestionId: string) => void
  onCreateTopics: (themeKeywordId: string) => void
  onDeleteKeyword: (keyword: Keyword) => void
  pageRankingResults?: KeywordLabel
  domain?: string
}

export const getColumns = <RecordType extends Keyword>({
  totalRecords,
  isLoadingKeywordLabel,
  onCreateTopics,
  onDeleteKeyword,
  onGetKeywordLabel,
  pageRankingResults,
  isDomainRemoved,
  domain,
  companyId,
  isGoogleConnected,
}: Props): ColumnsType<RecordType> => {
  return [
    {
      title: () => (
        <div className={s.title}>{`Keywords (${totalRecords})`}</div>
      ),
      render: (_, record) => {
        return (
          <KeywordDetailsPopover
            isLoading={isLoadingKeywordLabel}
            onGetKeywordLabel={() => onGetKeywordLabel(record.theme_keyword_id)}
            keywordLabel={pageRankingResults}
            topSearchResults={pageRankingResults?.top_ranking_details}
            selfRankingPages={pageRankingResults?.self_ranking_pages}
            domain={domain}
            primaryKeyword={record.name}
            updatedAt={pageRankingResults?.updated_at}
            isDomainRemoved={isDomainRemoved}
            companyId={companyId}
            isGoogleConnected={isGoogleConnected}
          >
            <div className={s.nameWrapper}>
              <div className={s.name}>
                {record.name}
                <Icon
                  icon="lucide:chevrons-right"
                  className={s.popoverIcon}
                  height={20}
                  width={20}
                />
              </div>
              {record?.self_top_position && (
                <GoogleRankPill googleRank={record.self_top_position} />
              )}
            </div>
          </KeywordDetailsPopover>
        )
      },
    },
    {
      title: () => (
        <div className={clsx(s.title, s.rightAlign)}>Monthly Volume</div>
      ),
      render: (_, record) => (
        <div className={clsx(s.volume, s.rightAlign)}>
          {formatLongNumber(record.volume, 0)}
        </div>
      ),
    },
    {
      title: () => <div className={s.title}>Trend</div>,
      width: 140,
      render: (_, record) => {
        return record.monthly_trends ? (
          <div className={s.trendBar}>
            <TrendsChart trends={record.monthly_trends} />
          </div>
        ) : (
          'NA'
        )
      },
    },
    {
      title: () => (
        <div className={clsx(s.title, s.intentTitle)}>
          Intent
          <Tooltip
            placement="bottom"
            title={() => {
              return (
                <div>Identify the user&apos;s goal behind a search query.</div>
              )
            }}
          >
            <Icon icon="lucide:info" className={s.info} />
          </Tooltip>
        </div>
      ),
      render: (_, record) => {
        return record.intent ? (
          <div className={s.intentWrapper}>
            {record.intent.split(',').map(intentVal => (
              <IntentPill value={intentVal} key={intentVal} />
            ))}
          </div>
        ) : (
          'NA'
        )
      },
    },
    {
      title: () => (
        <div className={clsx(s.title, s.rightAlign)}>Difficulty</div>
      ),
      render: (_, record) => (
        <div className={s.difficulty}>
          <KeywordDifficulty difficulty={record.difficulty_percentage} />
        </div>
      ),
      width: '10%',
    },
    {
      title: () => <div className={clsx(s.title, s.action)}>Action</div>,
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<Icon icon="lucide:plus" />}
          className={s.createButton}
          disabled={isDomainRemoved}
          onClick={() => onCreateTopics(record.theme_keyword_id)}
        >
          Create Topics
        </Button>
      ),
    },
    {
      title: '',
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          icon={<Icon icon="lucide:trash-2" />}
          onClick={() => onDeleteKeyword(record)}
          className={s.deleteBtn}
        />
      ),
    },
  ]
}
